import React from "react";
import PasswordInputBase from "./PasswordInputBase";
import { useValidate } from "formvalidation/hooks";
import { useCustomQuery, useSemanticUiInputRef } from "utilities/hookutils";

interface FirstPasswordInputProps {
  validatorKey: string
  value: string
  onChange: (value: string) => void
}

const FirstPasswordInput = ({ validatorKey, value, onChange }: FirstPasswordInputProps) => {
  const inputRef = useSemanticUiInputRef();

  const validatePasswordApi = useCustomQuery({
    queryKey: ["validatePassword", value],
    queryFn: () => window.CoreApi.Account.ValidatePasswordFormatting({ password: value }).then(r => r.data),
    staleTime: 1000 * 60 * 60, // cache result for 1 hour
    enabled: false
  })

  const validatePasswordFormat = async () => {
    if (value.trim() === "") {
      return RESX.GeneralWarnings.PleaseFillInThisField;
    }

    const validationResult = await validatePasswordApi.refetch();

    if (validationResult.data.hasErrors) {
      return validationResult.data.errors.map(e => e.message).join("\n");
    }

    return true;
  };

  const validation = useValidate({
    key: validatorKey,
    title: RESX.PersonalSettings.Password,
    value: value,
    validate: validatePasswordFormat,
    ref: inputRef.ref,
    type: "manual"
  })

  return <PasswordInputBase
    value={value}
    error={validation.error.errorMessage ?? validation.error.hasError}
    onChange={onChange}
    innerRef={inputRef}
    isLoading={validatePasswordApi.isFetching}
    onBlur={validation.validateAndSetError}
  />;
};

interface SecondPasswordInputProps extends FirstPasswordInputProps {
  firstPassword: string
}

const SecondPasswordInput = ({ value, onChange, validatorKey, firstPassword }: SecondPasswordInputProps) => {
  const inputRef = useSemanticUiInputRef();

  const validatePassword = async (newValue: string) => {
    const passwordsEqual = newValue === firstPassword;
    if (!passwordsEqual) {
      return RESX.PersonalSettings.PasswordsDontMatch;
    } else if (newValue.trim() === "") {
      return RESX.GeneralWarnings.PleaseFillInThisField;
    }
    return true;
  };

  const validator = useValidate({
    key: validatorKey,
    title: "Password",
    value: value,
    validate: validatePassword,
    ref: inputRef.ref,
    dependencies: [firstPassword, value]
  })

  return <PasswordInputBase
    value={value}
    error={validator.error.errorMessage ?? validator.error.hasError}
    onChange={onChange}
    innerRef={inputRef}
    isLoading={false}
    onBlur={validator.validateAndSetError}
  />;
};

const DoublePasswordInput = {
  FirstPasswordInput,
  SecondPasswordInput
};

export default DoublePasswordInput;